import { createTenantSchema } from '@cartly/shared';
import { Alert, Button, Card, SelectField, TextField } from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api/client';
import { useSubmit } from '../app/hooks';
import { useSession } from '../app/session';
import { Logo } from '../components/Logo';
import { COUNTRIES, CURRENCIES, defaultCurrencyFor, guessCountry } from './regions';

export function OnboardingPage() {
  const { state, refresh, logout } = useSession();
  const navigate = useNavigate();
  const hasTenants = state.status === 'active' && state.session.tenants.length > 0;
  const [form, setForm] = useState(() => {
    const country = guessCountry();
    return {
      name: '',
      country,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      defaultCurrency: defaultCurrencyFor(country),
    };
  });
  const { pending, error, fieldErrors, run, setFieldErrors } = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = createTenantSchema.safeParse(form);
    if (!parsed.success) {
      const fields: Record<string, string> = {};
      for (const issue of parsed.error.issues) fields[String(issue.path[0])] ??= issue.message;
      setFieldErrors(fields);
      return;
    }
    const created = await run(() => api.post<{ id: string }>('/tenants', parsed.data));
    if (!created) return;
    await refresh();
    navigate('/', { replace: true });
  };

  return (
    <div className="page-center">
      <div className="onboarding">
        <Logo variant="wordmark" className="onboarding__logo" />
        <div className="onboarding__intro">
          <h1 className="page-header__title">
            {hasTenants ? 'New organization' : 'Set up your organization'}
          </h1>
          <p className="page-header__description">
            An organization holds your stores, team, automations and billing. Pricing is shown in
            INR for businesses in India and USD elsewhere.
          </p>
        </div>
        <Card>
          <form className="stack" onSubmit={onSubmit} noValidate>
            {error && <Alert tone="critical">{error}</Alert>}
            <TextField
              label="Business name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors['name']}
              autoFocus
            />
            <div className="form-grid">
              <SelectField
                label="Country"
                value={form.country}
                options={COUNTRIES}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    country: e.target.value,
                    defaultCurrency: defaultCurrencyFor(e.target.value),
                  }))
                }
                error={fieldErrors['country']}
              />
              <SelectField
                label="Currency"
                value={form.defaultCurrency}
                options={CURRENCIES}
                onChange={(e) => setForm((f) => ({ ...f, defaultCurrency: e.target.value }))}
                error={fieldErrors['defaultCurrency']}
              />
              <TextField
                label="Time zone"
                fieldClassName="form-grid--full"
                value={form.timezone}
                onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                hint="IANA time zone, e.g. Asia/Kolkata. Used for scheduling and reports."
                error={fieldErrors['timezone']}
              />
            </div>
            <div className="form-actions">
              {hasTenants ? (
                <Link to="/" className="btn btn--ghost">
                  Cancel
                </Link>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => void logout().then(() => navigate('/login'))}
                >
                  Sign out
                </Button>
              )}
              <Button type="submit" variant="primary" loading={pending}>
                Create organization
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
